import Loading from '@/components/Loading/Loading';
import SendInputFailures from '@/components/Agents/SendValidator/Report/SendInputFailures';
import SendRunScope from '@/components/Agents/SendValidator/Report/SendRunScope';
import { aiReviewOutcomeOf, IN_VIVO_AI_SHORT } from '@/components/Agents/SendValidator/utils/sendAiUse';
import { sendDetectionResultOf, sendSessionResult } from '@/components/Agents/SendValidator/utils/sendResults';
import Select, { type SelectOption } from '@/components/UI/Select/Select';
import { sendDomainLabel, sendDomainOptions } from '@/components/Agents/SendValidator/utils/sendDomains';
import SendCoercionWarnings, { SEND_COERCION_REMEDY } from '@/components/Agents/SendValidator/SendCoercionWarnings';
import { detectionCoercionFilesOf } from '@/components/Agents/SendValidator/utils/sendCoercion';
import { _isNotEmpty, _notNil } from '@/littledash';
import type { AgentSessionApiId } from '@/model/Agent.model';
import { ApiService, ResponseType, type ApiErrorResponse } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC, useMemo, useState } from 'react';
import styles from './AgentSendConfirm.module.scss';

interface AgentSendConfirmProps {
  /** The SEND session whose detected bundle to confirm. */
  sessionApiId: AgentSessionApiId;
  /** Injected by the modal system; closes the modal. */
  closeModal: () => void;
  /**
   * Fired once the confirm has succeeded (the validate execution is now queued), before the modal
   * closes. Lets the caller refresh the session tree so the root "Validating" node re-spins its
   * loader immediately, signalling the newly-queued run.
   */
  onConfirmed?: () => void;
}

/**
 * Modal that confirms (or overrides) the per-file SENDIG domain a SEND bundle was detected as, before
 * validation runs. The `send_domain_confirm` tree node supplies only the session id; the detections
 * live on the detect execution, so the modal fetches the team-scoped domain-confirmation view. Rows
 * are labelled by the detection's `original_filename`, as the uploader is gone by the time this opens.
 *
 * Once `confirmed_domains` is present the panel reopens read-only, showing the ACTUAL confirmed
 * domains with no submit: the flow is strictly linear (detect → confirm → results) and must not be
 * re-run.
 *
 * It also fetches the session, because a run stopped on an unreadable file has nothing to confirm:
 * an input-failure result is listed with only a Close action, never the domain form or its empty
 * state, so an unreadable upload cannot look like a bundle with nothing detected. The load error shows
 * when the confirmation view fails, or when the session fails and nothing was detected.
 *
 * Files whose columns look rewritten by Excel are flagged above the rows, with what to do about it,
 * before the user commits to a validation run on them. The warning never blocks confirmation.
 *
 * A session holding a detection produced by an earlier version has nothing it can confirm either.
 * The server says so (`detection_status`), and the modal asks for the files to be uploaded again
 * rather than claiming nothing was detected.
 */
type ConfirmError = ApiErrorResponse<'POST /api/v1/ai-agents/send-validator/{sessionApiId}/confirm-domain'>['error'];

/**
 * The server's own reason for refusing a confirmation, such as a study out of scope or a detection
 * stored before scope was read, which it gives against the session; otherwise its first message.
 */
const refusalOf = (error: ConfirmError | undefined): string | undefined => {
  const errors = error?.response?.data?.errors;
  return errors?.session_api_id?.[0] ?? Object.values(errors ?? {}).flat()[0];
};

const AgentSendConfirm: FC<AgentSendConfirmProps> = ({ sessionApiId, closeModal, onConfirmed }) => {
  const { response, loading } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/send-validator/{sessionApiId}/domain-confirmation',
    path: { sessionApiId },
    options: {
      onError: {
        throw: false,
        preserveOriginalError: false,
        toast: false,
        capture: true,
        slug: 'agent-send-confirm-load',
      },
    },
  });

  const { response: sessionResponse, loading: sessionLoading } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}',
    path: { sessionApiId },
    options: {
      onError: {
        throw: false,
        preserveOriginalError: false,
        toast: false,
        capture: true,
        slug: 'agent-send-confirm-session-load',
      },
    },
  });
  const inputFailure = useMemo(() => {
    const sessionResult = sendSessionResult(sessionResponse?.type === 'success' ? sessionResponse.body : null);
    return sessionResult.kind === 'input_failure' ? sessionResult.result : null;
  }, [sessionResponse]);

  // A failed AI domain pick falls back to the top-ranked match, which the user should then check.
  const domainPickFailed = useMemo(() => {
    const detection = sendDetectionResultOf(sessionResponse?.type === 'success' ? sessionResponse.body : null);
    return aiReviewOutcomeOf(detection?.run_metadata) === 'failed';
  }, [sessionResponse]);

  const confirmation = useMemo(() => (response?.type === 'success' ? response.body : null), [response]);
  const detections = useMemo(() => confirmation?.detections ?? [], [confirmation]);
  const isEarlierVersion = confirmation?.detection_status === 'earlier_version';
  // The server refuses to confirm a study out of scope, so the step says so before anyone tries.
  const outOfScope = confirmation?.scope?.state === 'out_of_scope';

  // A non-empty map flips the panel to its read-only, non-resubmittable mode.
  const confirmedDomains = useMemo(
    () =>
      (confirmation?.confirmed_domains ?? []).reduce<Record<string, string>>((acc, confirmed) => {
        acc[confirmed.attachment_api_id] = confirmed.domain_code;
        return acc;
      }, {}),
    [confirmation]
  );
  const isConfirmed = _isNotEmpty(confirmedDomains);

  // Per-file chosen domain code, keyed by attachment id, seeded lazily from the detected domain.
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const domainFor = (attachmentApiId: string, detectedDomain: string): string =>
    confirmedDomains[attachmentApiId] ?? choices[attachmentApiId] ?? detectedDomain;

  const handleConfirm = async (): Promise<void> => {
    if (!_isNotEmpty(detections) || outOfScope) {
      return;
    }
    setConfirmError(null);
    setSubmitting(true);
    const files = detections.map((file) => ({
      attachment_api_id: file.attachment_api_id,
      domain_code: domainFor(file.attachment_api_id, file.detected_domain),
    }));
    const confirmResponse = await ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/send-validator/{sessionApiId}/confirm-domain',
      path: { sessionApiId },
      body: { files },
      options: {
        onError: { toast: false, throw: false, capture: true, slug: 'send-validator-confirm-domain' },
      },
    });
    setSubmitting(false);
    if (confirmResponse.type === ResponseType.Success && _notNil(confirmResponse.body)) {
      onConfirmed?.();
      closeModal();
      return;
    }
    setConfirmError(refusalOf(confirmResponse.error) ?? 'Failed to confirm domains.');
  };

  if (loading || sessionLoading) {
    return <Loading txt="Loading detected domains" />;
  }

  if (_notNil(inputFailure)) {
    return (
      <ModalContainer size="small" testKey="agent-send-confirm-modal">
        <ModalHeader
          title="SEND files could not be read"
          closeModal={closeModal}
          className="pa3 bb b--moon-gray bg-white"
        />
        <div className={styles.body} data-testid="agent-send-confirm-content">
          <SendInputFailures failures={inputFailure.failures} testId="agent-send-confirm__input-failures" />
        </div>
        <div className="pa3 bt b--moon-gray">
          <ModalActions cancelBtnText="Close" onCancel={closeModal} />
        </div>
      </ModalContainer>
    );
  }

  return (
    <ModalContainer size="small" testKey="agent-send-confirm-modal">
      <ModalHeader title="Confirm SEND domains" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />
      <div className={styles.body} data-testid="agent-send-confirm-content">
        {response?.type === 'error' || (sessionResponse?.type === 'error' && !_isNotEmpty(detections)) ? (
          <p className="gray tc" data-test-element="error">
            Couldn't load the detected domains.
          </p>
        ) : _isNotEmpty(detections) ? (
          <div className="flex flex-column" data-test-element="detections">
            {domainPickFailed && (
              <p
                className="f6 near-black ma0 mb3 pa2 bl bw2 b--orange"
                role="status"
                data-test-element="domain-pick-failed"
              >
                {IN_VIVO_AI_SHORT} couldn&apos;t help pick the domains, so each one below is the top-ranked match. Check
                each before you confirm.
              </p>
            )}
            <p className={`f6 ma0 mb3 ${isConfirmed ? styles.confirmedNote : 'dark-gray'}`}>
              {isConfirmed
                ? 'These domains have been confirmed for this session and can no longer be changed.'
                : outOfScope
                  ? "These are the detected domains, but they can't be confirmed for a study the validator can't check."
                  : 'Confirm the detected SENDIG domain for each file, or override it before validation runs.'}
            </p>
            <SendRunScope
              scope={confirmation?.scope}
              trcProxies={confirmation?.trc_proxies}
              stage="confirm"
              testId="agent-send-confirm__scope"
            />
            <SendCoercionWarnings
              files={detectionCoercionFilesOf(detections)}
              remedy={SEND_COERCION_REMEDY}
              testId="agent-send-confirm__coercion-warnings"
            />
            <ul className={styles.rows}>
              {detections.map((file) => {
                const selected = domainFor(file.attachment_api_id, file.detected_domain);
                return (
                  <li key={file.attachment_api_id} className={styles.row} data-test-element="detection-row">
                    <span className={styles.filename} data-test-element="detection-filename">
                      {file.original_filename}
                    </span>
                    {isConfirmed ? (
                      <span className={styles.confirmedDomain} data-test-element="detection-confirmed-domain">
                        {sendDomainLabel(selected)}
                      </span>
                    ) : (
                      <Select<SelectOption<string>>
                        isMulti={false}
                        className="w-100"
                        options={sendDomainOptions}
                        value={{ label: sendDomainLabel(selected), value: selected }}
                        aria-label={`Domain for ${file.original_filename}`}
                        placeholder="Search domains…"
                        isSearchable
                        onChange={(option) => {
                          if (_notNil(option)) {
                            setChoices((prev) => ({ ...prev, [file.attachment_api_id]: option.value }));
                          }
                        }}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
            {_notNil(confirmError) && (
              <div className="red f6 mt2" data-test-element="confirm-error">
                {confirmError}
              </div>
            )}
          </div>
        ) : isEarlierVersion ? (
          <p className="gray tc" data-test-element="earlier-version">
            {isConfirmed
              ? 'This upload was detected by an earlier version, so its confirmed domains cannot be shown.'
              : 'This upload was detected by an earlier version. Re-upload to continue.'}
          </p>
        ) : (
          <p className="gray tc" data-test-element="empty-state">
            No detected domains to confirm.
          </p>
        )}
      </div>
      <div className="pa3 bt b--moon-gray">
        {isConfirmed || isEarlierVersion ? (
          <ModalActions cancelBtnText="Close" onCancel={closeModal} />
        ) : (
          <ModalActions
            submitBtnText="Confirm domains"
            onSubmit={_isNotEmpty(detections) ? handleConfirm : undefined}
            submitButtonProps={{ disabled: submitting || !_isNotEmpty(detections) || outOfScope }}
            cancelBtnText="Cancel"
            onCancel={closeModal}
          />
        )}
      </div>
    </ModalContainer>
  );
};

export default AgentSendConfirm;
